import {Component, Inject, OnDestroy} from '@angular/core';
import {
    IdNameMxObj,
    MxCustomizer,
    MxDynamicFormService,
    MxFormCustomizerEvent, MxFormErrorMessageService, MxNotificationService,
    ObjectMetaData,
    OnBoFormMetadataReady,
    OnBoFormReady,
    UiDataObj,
} from 'mx-core';
import {PrlFinanceService} from '../../../../services/prl-finance.service';
import {combineLatestAll, of, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PrlTranslateService} from '../../../../../../shared/services/prl-translate.service';
import {FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';


@Component({
    selector: 'prl-edit-payment-account',
    standalone: false,
    templateUrl: './prl-edit-payment-account.component.html'
})
export class PrlEditPaymentAccountComponent implements OnDestroy, MxCustomizer<MxFormCustomizerEvent>,
    OnBoFormMetadataReady, OnBoFormReady {
    private form: FormGroup;
    private _destroy$$: Subject<null> = new Subject<null>();
    private paymentAccountData: any;
    private eligiblePaymentMethods: Array<IdNameMxObj> = [];
    private lastEnabledRawValue: { [key: string]: any } = {};


    constructor(protected readonly translateService: PrlTranslateService,
                private readonly notificationService: MxNotificationService,
                private dynamicFormService: MxDynamicFormService,
                private financeService: PrlFinanceService,
                private dialogRef: MatDialogRef<PrlEditPaymentAccountComponent>,
                @Inject(MAT_DIALOG_DATA) public data: { id: number },
                private errorService: MxFormErrorMessageService
    ) {}

    onCustomize(event: MxFormCustomizerEvent): void {
        event
            .customizeBoFormConfig({title: this.translateService.getTranslatedLabel('label.finance.payment.account.edit')})
        of(
            this.dynamicFormService.getUIData('com.playtech.mexos.contract.finance.EditPaymentAccountPrlObj'),
            this.financeService.getPaymentAccount(this.data.id),
            this.financeService.getPaymentMethods()
        ).pipe(takeUntil(this._destroy$$), combineLatestAll())
            .subscribe(([uiData, accountData, eligibleMethods]) => {

                this.paymentAccountData = accountData;
                this.eligiblePaymentMethods = eligibleMethods as Array<IdNameMxObj>;
                event.fieldsData.next({
                    objectMetaSource: uiData as UiDataObj,
                    initialData: this.paymentAccountData
                });
            });
        event.customizeProps({
            addPrefixToTitle: false,
            submitFunction: (form: FormGroup): void => {
                const payload = {...form.getRawValue(), ...this.lastEnabledRawValue};
                this.financeService.updatePaymentAccount(this.data.id, payload)
                    .subscribe({
                        next: res => {
                            this.dialogRef.close();
                        },
                        error: err => {
                            if (err?.status === 400 && err.error?.fieldErrors) {
                                this.errorService.setServerErrors(err.error, form);
                            }
                            form.enable();
                        }
                    });
            },
            showNavigation: false,
            customNavigation: {
                onCancel: () => {
                    this.dialogRef.close();
                }
            }
        })
            .customizeActions({
                submit: {
                    text: ()=> this.translateService.getTranslatedLabel('label.submit'),
                    visible: true,
                    disableFunction: this.isSubmitDisabled
                },
                cancel: {
                    text: ()=> this.translateService.getTranslatedLabel('label.cancel'),
                    visible: true
                }
            });
        event.customizeFormMode('edit')
            .addCustomizer(this);
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    boOnMetadataReady(metaData: ObjectMetaData): ObjectMetaData {
        const status = this.paymentAccountData?.statusName;
        return ObjectMetaData.modifyFields(metaData,
            field => {
                if (field.name === 'isDefault' && this.shouldBlockSetDefault()) {
                    field.disabled = true;
                }

                if (field.name === 'properties') {
                    field.sublist
                        .find(group => group.name === this.paymentAccountData?.paymentMethod)?.sublist
                        .filter(methodField => status === 'Approved'
                            || (status === 'Pending' && methodField.type === 'password'))
                        .forEach(methodField => methodField.disabled = true);
                }
                return field;
            });
    }

    boOnFormReady(form: FormGroup): void {
        this.form = form;
        form.setAsyncValidators(this.errorService.asyncValidator(this.financeService, 'payment-accounts/account-data/edit/validate'));

        Object.entries(this.paymentAccountData || {}).forEach(([key, value]) => {
            const control = this.form.get(key);
            if (control) {
                control.setValue(value, {emitEvent: false});
            }
        });
        const secureIdControl = this.form.get('secureId');
        if (secureIdControl) {
            const retypeControl = new FormControl(secureIdControl.value, [
                (ctrl) => {
                    const pass = secureIdControl.value;
                    const retype = ctrl.value;
                    return pass === retype ? null : { matchPassword: true };
                }
            ]);
            retypeControl.valueChanges
                .pipe(takeUntil(this._destroy$$))
                .subscribe(() => {
                    retypeControl.markAsTouched();
                    retypeControl.updateValueAndValidity({ emitEvent: false });
                });
            this.form.addControl('secureId retype', retypeControl);
        }

        form.valueChanges
            .pipe(takeUntil(this._destroy$$))
            .subscribe(() => {
                if (!form.enabled) {
                    return;
                }
                const raw = form.getRawValue();
                Object.keys(raw).forEach(key => {
                    const value = raw[key];
                    if (value !== null && value !== undefined && value !== '') {
                        this.lastEnabledRawValue[key] = value;
                    }
                });
            });
    }

    boOnBeforeSubmit(form: FormGroup): void {
        Object.entries(this.lastEnabledRawValue).forEach(([name, value]) => {
            const control = form.get(name);
            if (control && (control.value === null || control.value === undefined || control.value === '')) {
                control.setValue(value, {emitEvent: false});
            }
        });
    }

    private shouldBlockSetDefault(): boolean {
        if (this.paymentAccountData?.isDefault === 'true') {
            return true;
        }
        const paymentMethodName = this.paymentAccountData?.paymentMethod;
        if (!paymentMethodName) {
            return false;
        }
        return !this.eligiblePaymentMethods.some(method => method.name === paymentMethodName);
    }

    private isSubmitDisabled = (): boolean => !this.form || !this.form.dirty || this.form.invalid;
}